import React from 'react';
import { ButtonProps, Button as HeroButton } from '@heroui/react';
import { cn } from '@utils/cn';
import type { Hierarchy, Size, SizeClasses } from './types';

// ─── Design system ────────────────────────────────────────────────────────────
// Fuente: Figma "01. Botones" (48:2), component set 49:322.
// Especificación completa: .github/docs/design-system/MIGRATION-STATUS.md
//
// Común a todas las jerarquías: alto 44, radio 50 (= pill), padding 8 vertical /
// 10 horizontal, gap 5 entre partes, iconos 18×18, y el rol tipográfico `button`
// (Montserrat ExtraBold 11).
//
// El rol `button` de la escala tipográfica está marcado como "Caps" en Figma,
// pero **no se aplica `uppercase` acá**: las mayúsculas se deciden por caso desde
// fuera del componente. Muchos usos son enlaces en línea donde no corresponden.
//
// ⚠️ El design system NO documenta estado hover. Los `hover:` de abajo son UX
// necesaria, no especificación: se derivan de los tokens de marca.

const DS_BASE_CLASSES = 'h-11 rounded-full px-2.5 py-2 gap-[5px] shrink-0';

// ⚠️ La tipografía va en un elemento INTERNO, no en el `className` del botón.
//
// HeroUI corre su propio `tailwind-merge` dentro del componente, con una config
// que solo conoce sus tamaños (`tiny`/`small`/`medium`/`large`) y con los
// componentes ya compilados — no acepta configuración desde fuera. Al no
// reconocer `text-button` lo toma por un color, choca con `text-fg-on-brand` y
// **descarta el tamaño**. Verificado llamando a su función de tema real:
//
//   btn({ color:'primary', variant:'solid', class:'… text-button text-fg-on-brand' })
//     → sobrevive `text-small` (14px). `text-button` BORRADO.
//
// En elementos distintos no compiten, así que el tamaño llega intacto. `Label`
// también aporta el peso: el token lo trae, y aquí no hay ningún `font-normal`
// de HeroUI que lo pise (ese vive en el <button>, no en el <span>).
const LABEL_CLASSES = 'text-button';

// El estado deshabilitado se declara dos veces a propósito: HeroUI lo marca con
// `data-disabled`, pero el atributo nativo `disabled` también puede llegar por
// props. No se puede factorizar con un helper: Tailwind escanea el código fuente
// buscando clases literales y no detecta las construidas por concatenación.
// El color del texto se declara explícito en las tres jerarquías, sin depender
// de lo que HeroUI aplique por debajo:
//   primary   → neutral/0 (blanco)      secondary → neutral/80 (oscuro)
//   tertiary  → brand/500 (azul)
const hierarchyClasses: Record<Hierarchy, string> = {
  // Fondo brand/500 con texto blanco.
  // Inactivo: fondo brand/200 y texto brand/25 — ninguno tiene token semántico,
  // de ahí que se usen las primitivas.
  primary: [
    'bg-surface-brand text-fg-on-brand',
    'hover:bg-surface-brand-hover',
    'disabled:bg-brand-200 disabled:text-brand-25 disabled:opacity-100',
    'data-[disabled=true]:bg-brand-200 data-[disabled=true]:text-brand-25 data-[disabled=true]:opacity-100',
    'disabled:pointer-events-none',
  ].join(' '),

  // Borde de 1.5px y texto, ambos neutral/80. Inactivo: neutral/50.
  // `neutral-80` es de la familia legada, pero su valor coincide exactamente con
  // el `neutral/80` de Figma: es parte del subconjunto ya alineado.
  secondary: [
    'border-[1.5px] border-neutral-80 text-neutral-80 bg-transparent',
    'hover:bg-surface-muted',
    'disabled:border-stroke-strong disabled:text-fg-disabled disabled:bg-transparent disabled:opacity-100',
    'data-[disabled=true]:border-stroke-strong data-[disabled=true]:text-fg-disabled data-[disabled=true]:opacity-100',
    'disabled:pointer-events-none',
  ].join(' '),

  // Solo texto, brand/500. Ojo: NO es `text-fg-brand`, que vale brand/600.
  // El hover actúa SOLO sobre el texto: el fondo se mantiene transparente, y hay
  // que neutralizar el tinte que HeroUI aplica en su variante `light`.
  tertiary: [
    'text-brand-500 bg-transparent',
    'hover:text-fg-brand-hover',
    'data-[hover=true]:bg-transparent hover:bg-transparent',
    'disabled:text-fg-disabled disabled:bg-transparent disabled:opacity-100',
    'data-[disabled=true]:text-fg-disabled data-[disabled=true]:opacity-100',
    'disabled:pointer-events-none',
  ].join(' '),
};

// Se apoya en el tema de HeroUI, que ya lleva la paleta de Figma: con
// `color="primary"` su variante `solid` da fondo brand/500 con texto blanco, y
// `light` da texto brand/500. Las dos ya son correctas sin override.
const hierarchyHeroProps: Record<
  Hierarchy,
  { color: ButtonProps['color']; variant: ButtonProps['variant'] }
> = {
  primary: { color: 'primary', variant: 'solid' },
  secondary: { color: 'default', variant: 'bordered' },
  tertiary: { color: 'primary', variant: 'light' },
};

// `sm` = 150px, el ancho del botón en el design system. `md` y `lg` no existen
// en el design system.
const sizeClasses: SizeClasses = {
  sm: 'min-w-[9.375rem]',
  md: 'min-w-[11.25rem]',
  lg: 'min-w-[22.5rem]',
};

// ─── Botón fuera de React ─────────────────────────────────────────────────────

// Lo que HeroUI aporta al <button> y que forma parte de cómo se ve y se siente
// el botón del design system: atenuado al pasar por encima, encogido al pulsar y
// la transición entre ambos.
const INTERACTION_CLASSES =
  'inline-flex items-center justify-center whitespace-nowrap cursor-pointer transition-transform-colors-opacity hover:opacity-hover active:scale-[0.97]';

/**
 * Clases del botón del design system para los contextos que renderizan su propio
 * `<button>` y no pueden montar `CustomButton` — hoy solo SweetAlert2, que crea
 * los suyos fuera del árbol de React. Comparten fuente con el componente, así que
 * un cambio de jerarquía llega a los dos.
 */
export const buttonClasses = (hierarchy: Hierarchy): string =>
  cn(
    DS_BASE_CLASSES,
    LABEL_CLASSES,
    INTERACTION_CLASSES,
    sizeClasses.sm,
    hierarchyClasses[hierarchy],
  );

// ─── Props ────────────────────────────────────────────────────────────────────

export interface CustomButtonProps extends Omit<ButtonProps, 'children'> {
  label: string;
  icon?: React.JSX.Element;
  iconPosition?: 'start' | 'end';
  isTextOnly?: boolean;
  className?: string;
  /**
   * Jerarquía del design system. El estado (Activo / Inactivo / Loader) se
   * deriva de `isDisabled` e `isLoading`.
   */
  hierarchy?: Hierarchy;
  customSize?: Size;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const CustomButton = ({
  label,
  iconPosition = 'start',
  icon,
  isTextOnly = false,
  hierarchy = 'primary',
  customSize = 'sm',
  className = '',
  ...props
}: CustomButtonProps) => {
  const iconElement = icon ? icon : null;
  const startContent = iconPosition === 'start' ? iconElement : undefined;
  const endContent = iconPosition === 'end' ? iconElement : undefined;

  return (
    <HeroButton
      {...hierarchyHeroProps[hierarchy]}
      startContent={startContent}
      endContent={endContent}
      className={cn(
        DS_BASE_CLASSES,
        sizeClasses[customSize],
        hierarchyClasses[hierarchy],
        className,
      )}
      {...props}
    >
      <span className={LABEL_CLASSES}>{label}</span>
    </HeroButton>
  );
};
